import React, { useEffect, useRef, useState } from "react";
import {
  X,
  Trash2,
  Save,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash,
  Maximize,
  Camera,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { measureAny, captureCurrentFrame } from "../lib/measure";
import api from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u || ""}`);

// Build a media list from card.src + card.gallery so we can navigate.
function mediaListOf(card) {
  if (!card) return [];
  const list = [];
  if (card.src) list.push({ type: card.type || "image", src: card.src, thumbnail: card.thumbnail || "" });
  for (const g of card.gallery || []) {
    if (g?.src) list.push({ type: g.type || "image", src: g.src, thumbnail: g.thumbnail || "" });
  }
  return list;
}

export default function MediaLightbox({ card, editable, onClose, onChange, onDelete }) {
  const [draft, setDraft] = useState(card);
  const [idx, setIdx] = useState(0);
  const [savingThumb, setSavingThumb] = useState(false);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setDraft(card);
    setIdx(0);
  }, [card]);

  useEffect(() => {
    const onKey = (e) => {
      if (!card) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight")
        setIdx((i) => Math.min(mediaListOf(card).length - 1, i + 1));
    };
    if (card) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [card, onClose]);

  if (!card || !draft) return null;

  const media = mediaListOf(draft);
  const current = media[idx] || media[0] || { type: "image", src: "" };
  const hasGallery = media.length > 1;

  const updateField = (k, v) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    if (editable) onChange({ [k]: v });
  };

  const fitToMedia = async () => {
    if (!current?.src) return;
    const dim = await measureAny(current.type, current.src);
    onChange({ width: dim.width, height: dim.height });
    setDraft((d) => ({ ...d, width: dim.width, height: dim.height }));
    toast("Card resized to media");
  };

  const captureFrameAsThumbnail = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) {
      toast("Wait until the video has loaded");
      return;
    }
    setSavingThumb(true);
    try {
      // Pause first so the frame is stable.
      try { v.pause(); } catch { /* ignore */ }
      const blob = await captureCurrentFrame(v);
      if (!blob) throw new Error("Couldn't grab frame");
      const file = new File([blob], `thumb-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      const data = await api.uploads.upload(file);
      const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      // Persist immediately on the card.
      onChange({ thumbnail: fullUrl });
      setDraft((d) => ({ ...d, thumbnail: fullUrl }));
      toast("Thumbnail saved from this frame");
    } catch (err) {
      toast("Couldn't save thumbnail");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSavingThumb(false);
    }
  };

  const removeGalleryItem = (mediaIndex) => {
    // mediaIndex is index in `media` (0 is primary src).
    if (mediaIndex === 0) {
      // Promote first gallery item to primary
      const newGallery = [...(draft.gallery || [])];
      const next = newGallery.shift();
      if (!next) return; // can't remove the only image
      const patch = {
        src: next.src,
        type: next.type,
        thumbnail: next.thumbnail || "",
        gallery: newGallery,
      };
      setDraft((d) => ({ ...d, ...patch }));
      onChange(patch);
    } else {
      const newGallery = [...(draft.gallery || [])];
      newGallery.splice(mediaIndex - 1, 1);
      setDraft((d) => ({ ...d, gallery: newGallery }));
      onChange({ gallery: newGallery });
    }
    setIdx((i) => Math.max(0, i - (i === mediaIndex ? 1 : 0)));
    toast("Image removed from card");
  };

  const addGalleryFiles = async (files) => {
    if (!files?.length) return;
    try {
      const newItems = [];
      for (const f of Array.from(files)) {
        const data = await api.uploads.upload(f);
        newItems.push({
          type: f.type.startsWith("video/") ? "video" : "image",
          src: absUrl(data.url),
          thumbnail: "",
        });
      }
      const newGallery = [...(draft.gallery || []), ...newItems];
      setDraft((d) => ({ ...d, gallery: newGallery }));
      onChange({ gallery: newGallery });
      toast(`${newItems.length} added to this card`);
    } catch {
      toast("Upload failed");
    }
  };

  const onShellClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col md:flex-row items-stretch p-2 sm:p-4 md:p-8 gap-2 sm:gap-4 bg-[var(--ink)]/85 md:bg-[var(--ink)]/80 md:backdrop-blur-sm overflow-y-auto md:overflow-hidden"
      onClick={onShellClick}
    >
      {/* media side */}
      <div
        className="relative rounded-xl overflow-hidden bg-black flex items-center justify-center w-full md:flex-1 h-[50vh] sm:h-[55vh] md:h-auto shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {current.type === "video" ? (
          <video
            key={current.src}
            ref={videoRef}
            src={current.src}
            className="w-full h-full object-contain"
            controls
            playsInline
            preload="metadata"
            poster={current.thumbnail || undefined}
          />
        ) : (
          <img
            key={current.src}
            src={current.src}
            alt={card.title}
            className="max-w-full max-h-full object-contain"
            loading="lazy"
            decoding="async"
          />
        )}

        {/* gallery arrows */}
        {hasGallery && (
          <>
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 disabled:opacity-30 flex items-center justify-center btn-pop"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIdx((i) => Math.min(media.length - 1, i + 1))}
              disabled={idx === media.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 disabled:opacity-30 flex items-center justify-center btn-pop"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/60 text-white text-[11px] tabular-nums tracking-wide">
              {idx + 1} / {media.length}
            </div>
          </>
        )}

        {/* close button (mobile) — sticks to media corner so it's always reachable */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:hidden h-9 w-9 rounded-full bg-white/90 flex items-center justify-center btn-pop z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* thumbnail strip */}
        {hasGallery && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-3 max-w-[90%] flex items-center gap-2 px-2 py-2 rounded-xl bg-black/55 md:backdrop-blur overflow-x-auto no-scrollbar">
            {media.map((m, i) => (
              <button
                key={`${m.src}-${i}`}
                onClick={() => setIdx(i)}
                className={`relative shrink-0 h-14 w-14 rounded-md overflow-hidden border-2 transition-colors ${
                  i === idx ? "border-[var(--vermillion)]" : "border-transparent opacity-70 hover:opacity-100"
                }`}
                title={`Item ${i + 1}`}
              >
                {m.type === "video" ? (
                  <div className="w-full h-full bg-black flex items-center justify-center text-white text-[10px]">
                    VIDEO
                  </div>
                ) : (
                  <img src={m.src} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}

        {editable && (
          <div className="absolute bottom-3 right-3 flex items-center gap-2 flex-wrap justify-end max-w-[calc(100%-1.5rem)]">
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={(e) => addGalleryFiles(e.target.files)}
            />
            {current.type === "video" && (
              <button
                onClick={captureFrameAsThumbnail}
                disabled={savingThumb}
                className="h-9 px-3 rounded-full bg-[var(--vermillion)] text-white flex items-center gap-1 text-xs btn-pop disabled:opacity-60"
                title="Pause the video at any frame, then click to set that frame as the card thumbnail"
              >
                <Camera className="w-3.5 h-3.5" />
                {savingThumb ? "Saving…" : "Use this frame as thumb"}
              </button>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="h-9 px-3 rounded-full bg-white/90 flex items-center gap-1 text-xs btn-pop"
            >
              <Plus className="w-3.5 h-3.5" /> Add image
            </button>
            <button
              onClick={fitToMedia}
              className="h-9 px-3 rounded-full bg-white/90 flex items-center gap-1 text-xs btn-pop"
              title="Resize card to fit this media"
            >
              <Maximize className="w-3.5 h-3.5" /> Fit to media
            </button>
            {media.length > 1 && (
              <button
                onClick={() => removeGalleryItem(idx)}
                className="h-9 px-3 rounded-full bg-white/90 text-red-700 flex items-center gap-1 text-xs btn-pop"
              >
                <Trash className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
        )}
      </div>

      {/* info side */}
      <aside
        className="w-full md:max-w-md flex flex-col bg-[var(--paper)] rounded-xl overflow-hidden card-stack md:h-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--ink)]/10">
          <span className="text-[10px] uppercase tracking-[0.22em] text-ink-soft">
            {card.category} · {hasGallery ? `${media.length} items` : card.type}
          </span>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-[var(--paper-deep)] flex items-center justify-center btn-pop"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-6 space-y-5">
          {editable ? (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Title</label>
                <Input
                  value={draft.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  className="mt-1 font-display text-2xl h-auto py-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Client</label>
                  <Input
                    value={draft.client || ""}
                    onChange={(e) => updateField("client", e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Year</label>
                  <Input
                    value={draft.year || ""}
                    onChange={(e) => updateField("year", e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Category label</label>
                <Input
                  value={draft.category || ""}
                  onChange={(e) => updateField("category", e.target.value)}
                  placeholder="Video, Graphic, Branding, …"
                  className="mt-1"
                  data-testid="lightbox-category-input"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {["video", "graphic", "social", "branding", "motion", "print"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => updateField("category", p)}
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border transition-colors ${
                        (draft.category || "").toLowerCase() === p
                          ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                          : "bg-white text-ink-soft border-[var(--ink)]/15 hover:border-[var(--ink)]/40"
                      }`}
                      data-testid={`category-preset-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Description</label>
                <Textarea
                  rows={6}
                  value={draft.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Tags (comma separated)</label>
                <Input
                  value={(draft.tags || []).join(", ")}
                  onChange={(e) =>
                    updateField(
                      "tags",
                      e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                    )
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Primary media URL</label>
                <Input
                  value={draft.src || ""}
                  onChange={(e) => updateField("src", e.target.value)}
                  className="mt-1"
                />
              </div>
              {draft.type === "video" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Thumbnail URL</label>
                  <Input
                    value={draft.thumbnail || ""}
                    onChange={(e) => updateField("thumbnail", e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="font-display text-4xl leading-[0.95]">{card.title}</h2>
              <div className="flex gap-4 text-xs text-ink-soft">
                <span>
                  <span className="uppercase tracking-[0.18em] text-[10px] block text-ink-soft/70">Client</span>
                  {card.client || "—"}
                </span>
                <span>
                  <span className="uppercase tracking-[0.18em] text-[10px] block text-ink-soft/70">Year</span>
                  {card.year || "—"}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-ink-soft whitespace-pre-wrap">
                {card.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {(card.tags || []).map((t) => (
                  <Badge
                    key={t}
                    variant="secondary"
                    className="rounded-full bg-[var(--paper-deep)] text-[var(--ink)] hover:bg-[var(--paper-deep)]"
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </>
          )}
        </div>

        {editable && (
          <div className="px-5 py-4 border-t border-[var(--ink)]/10 flex items-center justify-between bg-[var(--paper-deep)]/50">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-700 hover:text-red-800 hover:bg-red-50 btn-pop"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
            <Button
              size="sm"
              onClick={onClose}
              className="bg-[var(--vermillion)] hover:bg-[var(--vermillion-soft)] text-white btn-pop"
            >
              <Save className="w-4 h-4 mr-1" /> Done
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}
